import Dexie, { type Table } from 'dexie';
import type { AppState } from '../../core/storage/repository';

const DATABASE_NAME = 'ayo_baca_app_v1';
const STATE_KEY = 'current';
const LEGACY_STORAGE_KEY = 'ayo_baca_app_state_v1';
const MIGRATION_MARKER_KEY = 'legacy-local-storage-v1';
const STORAGE_SCHEMA_VERSION = 1;

interface AppStateRecord {
  id: string;
  state: AppState;
  updatedAt: string;
  schemaVersion: number;
}

interface StorageMetadataRecord {
  id: string;
  completedAt: string;
  schemaVersion: number;
}

class AyoBacaDatabase extends Dexie {
  appStates!: Table<AppStateRecord, string>;
  storageMetadata!: Table<StorageMetadataRecord, string>;

  constructor() {
    super(DATABASE_NAME);
    this.version(1).stores({
      appStates: 'id, updatedAt',
    });
    this.version(2).stores({
      appStates: 'id, updatedAt',
      storageMetadata: 'id',
    });
  }
}

const database = typeof indexedDB === 'undefined' ? null : new AyoBacaDatabase();

function isAppState(value: unknown): value is AppState {
  if (!value || typeof value !== 'object') return false;
  const state = value as Partial<AppState>;
  const profile = state.profile;
  const garden = state.garden;
  const progress = state.progress;
  const settings = state.settings;
  return Boolean(
    profile &&
      typeof profile.id === 'string' &&
      typeof profile.nickname === 'string' &&
      typeof profile.learningLevel === 'string' &&
      typeof profile.avatarId === 'string' &&
      typeof profile.stars === 'number' &&
      garden &&
      typeof garden.stage === 'number' &&
      typeof garden.waterCount === 'number' &&
      typeof garden.totalPoints === 'number' &&
      Array.isArray(garden.unlockedBadges) &&
      progress &&
      Array.isArray(progress.lettersFamiliar) &&
      Array.isArray(progress.syllablesFamiliar) &&
      Array.isArray(progress.wordsMastered) &&
      typeof progress.learningDays === 'number' &&
      typeof progress.learningMinutes === 'number' &&
      typeof progress.lastPracticedDate === 'string' &&
      settings &&
      (settings.speechRate === 'normal' || settings.speechRate === 'slow') &&
      typeof settings.speechEnabled === 'boolean' &&
      typeof settings.soundEffects === 'boolean' &&
      typeof settings.retainDiscoveryImages === 'boolean' &&
      typeof settings.parentPin === 'string' &&
      Array.isArray(state.discoveredWords),
  );
}

function readLegacyState(): AppState | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isAppState(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Legacy localStorage migration was skipped:', error);
    return null;
  }
}

export async function hydrateAppState(fallback: AppState): Promise<AppState> {
  if (!database) return fallback;

  try {
    const existing = await database.appStates.get(STATE_KEY);
    if (existing && isAppState(existing.state)) {
      await database.storageMetadata.put({
        id: MIGRATION_MARKER_KEY,
        completedAt: new Date().toISOString(),
        schemaVersion: STORAGE_SCHEMA_VERSION,
      });
      return existing.state;
    }

    const legacyState = readLegacyState();
    const state = legacyState ?? fallback;
    await database.transaction('rw', database.appStates, database.storageMetadata, async () => {
      await database.appStates.put({
        id: STATE_KEY,
        state,
        updatedAt: new Date().toISOString(),
        schemaVersion: STORAGE_SCHEMA_VERSION,
      });
      await database.storageMetadata.put({
        id: MIGRATION_MARKER_KEY,
        completedAt: new Date().toISOString(),
        schemaVersion: STORAGE_SCHEMA_VERSION,
      });
    });
    return state;
  } catch (error) {
    console.error('Failed to hydrate IndexedDB state:', error);
    return fallback;
  }
}

export async function persistAppState(state: AppState): Promise<void> {
  if (!database) return;

  try {
    await database.appStates.put({
      id: STATE_KEY,
      state,
      updatedAt: new Date().toISOString(),
      schemaVersion: STORAGE_SCHEMA_VERSION,
    });
  } catch (error) {
    console.error('Failed to persist IndexedDB state:', error);
  }
}

export async function replacePersistedAppState(state: AppState): Promise<void> {
  if (!database) return;

  await database.transaction('rw', database.appStates, async () => {
    await database.appStates.clear();
    await database.appStates.put({
      id: STATE_KEY,
      state,
      updatedAt: new Date().toISOString(),
      schemaVersion: STORAGE_SCHEMA_VERSION,
    });
  });
}

export async function exportPersistedAppState(fallback: AppState): Promise<string> {
  const state = await hydrateAppState(fallback);
  return JSON.stringify({
    exportedAt: new Date().toISOString(),
    schemaVersion: STORAGE_SCHEMA_VERSION,
    state,
  }, null, 2);
}

export async function deleteAllPersistedData(): Promise<void> {
  if (database) {
    await database.transaction('rw', database.appStates, database.storageMetadata, async () => {
      await database.appStates.clear();
      await database.storageMetadata.clear();
    });
  }

  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  }
}
